import { useState } from 'react';
import { api, ApiError } from '../api';
import { get_renderer, type AppPayload } from '../renderers';
import JsonEditor from './JsonEditor';

type Obj = Record<string, unknown>;

// One app's card on UserDetail — read (renderer) with Edit / Remove, edits via the raw-JSON
// editor (per the docs/admin.md §6.1 four-state table). captomi_office is tenant-scoped post-#15
// and edited on TenantDetail via SchemaForm; the typed-AppDataForm path was retired with it.
export default function UserAppSection({
  tenant_id,
  user_id,
  app,
  on_changed,
}: {
  tenant_id: string;
  user_id: string;
  app: AppPayload;
  on_changed: () => void;
}) {
  const [editing, set_editing] = useState(false);
  const [removing, set_removing] = useState(false);
  const [remove_busy, set_remove_busy] = useState(false);
  const [remove_error, set_remove_error] = useState<string | null>(null);

  const renderer = get_renderer(app.app_name);
  const base = `/api/tenants/${tenant_id}/users/${user_id}/apps/${app.app_name}`;

  async function save_json(data: Obj) {
    await api.put(base, { data });
    set_editing(false);
    on_changed();
  }
  async function do_remove() {
    set_remove_busy(true);
    set_remove_error(null);
    try {
      await api.del(`${base}?confirm=1`);
      set_removing(false);
      on_changed();
    } catch (err) {
      set_remove_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_remove_busy(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <span className="eyebrow">{app.app_name}</span>
        {renderer.app_name === '*' && (
          <span className="muted" style={{ fontSize: 12 }}>
            (default renderer)
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {!editing && (
            <>
              <button
                className="secondary"
                style={{ padding: '4px 10px', fontSize: 12 }}
                onClick={() => set_editing(true)}
              >
                Edit
              </button>
              <button
                className="secondary"
                style={{ padding: '4px 10px', fontSize: 12 }}
                onClick={() => set_removing(true)}
              >
                Remove
              </button>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <JsonEditorWrap initial={app.data} onSave={save_json} onCancel={() => set_editing(false)} />
      ) : (
        renderer.render(app)
      )}

      {removing && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,26,20,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={() => !remove_busy && set_removing(false)}
        >
          <div
            className="card"
            style={{ maxWidth: 460, width: '90%', background: 'var(--bg)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Remove {app.app_name}?</h3>
            <p style={{ fontSize: 14 }}>
              Hard-deletes this app's data and subcollections for the user. This cannot be
              undone.
            </p>
            {remove_error && <div className="error">{remove_error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                className="secondary"
                onClick={() => set_removing(false)}
                disabled={remove_busy}
              >
                Cancel
              </button>
              <button onClick={do_remove} disabled={remove_busy}>
                {remove_busy ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// JsonEditor manages its text; this wrapper owns the save busy/error state.
function JsonEditorWrap({
  initial,
  onSave,
  onCancel,
}: {
  initial: Obj;
  onSave: (data: Obj) => Promise<void>;
  onCancel: () => void;
}) {
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  async function handle(data: Obj) {
    set_busy(true);
    set_error(null);
    try {
      await onSave(data);
    } catch (err) {
      set_error(err instanceof ApiError ? err.reason : String(err));
      set_busy(false);
    }
  }
  return (
    <JsonEditor initial={initial} busy={busy} error={error} onSave={handle} onCancel={onCancel} />
  );
}
